// Phase F.3 — Send-out dialog + "Last sent to" header chip + audit panel.
//
// One file ships three React surfaces:
//   1. <SendOutDialog rid variant onClose /> — modal opened from the
//      forwarder PDF buttons. Calls the resolver, lets the user pick/
//      override recipients, POST /send-outs.
//   2. <SendOutHeaderChip rid /> — last-3 inline chip on the deal header
//      ("Sent 3 hrs ago to Bob · email") so the operator doesn't double-
//      send. Click → expands the audit panel.
//   3. <SendOutsPanel rid /> — full audit trail (chronological).
import React, { useMemo, useState } from "react";
import {
  Send, X, Mail, Plus, Trash2, AlertCircle, Loader2, CheckCircle2,
  Clock, History, ArrowRight, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSendOuts, useRecentSendOuts, useCreateSendOut } from "../api/sendOuts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #0a0a0a)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "#fff",
  border: "1px solid rgba(0,0,0,0.15)",
  color: "#0a0a0a",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "8px 10px",
  borderRadius: 5,
  width: "100%",
};

/* ─── Header chip ───────────────────────────────────────────────────── */

export const SendOutHeaderChip = ({ rid, onOpenTrail }) => {
  // Snapshot "now" so we don't churn on every render.
  const [nowMs] = useState(() => Date.now());
  const { data } = useRecentSendOuts(rid, 3);
  const items = data?.items || [];
  if (items.length === 0) return null;
  const last = items[0];
  const relTime = (() => {
    const ms = nowMs - Date.parse(last.sent_at);
    if (ms < 60_000) return "just now";
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)} hr${ms >= 7_200_000 ? "s" : ""} ago`;
    return `${Math.floor(ms / 86_400_000)} day${ms >= 172_800_000 ? "s" : ""} ago`;
  })();
  const toName = last.recipients?.find((r) => r.kind === "to")?.name
    || last.recipients?.find((r) => r.kind === "to")?.email
    || "recipient";

  return (
    <button
      type="button"
      onClick={onOpenTrail}
      className="inline-flex items-center gap-2 px-2.5 py-1 rounded"
      style={{
        background: "rgba(10,135,84,0.10)",
        border: "1px solid rgba(10,135,84,0.35)",
        color: "#0a8754",
        ...mono(10, "#0a8754", 700, "0.6px"),
        textTransform: "uppercase",
        cursor: "pointer",
      }}
      data-testid="send-out-header-chip"
      title={items.map((i) => `${i.variant} → ${i.recipients?.[0]?.name || i.recipients?.[0]?.email} (${i.sent_at})`).join("\n")}
    >
      <CheckCircle2 size={11} />
      Sent {relTime} → {toName}
      {items.length > 1 && <span style={mono(9, "#0a8754")}>+{items.length - 1}</span>}
      <span style={mono(9, "#0a8754", 700)}>· {last.channel}</span>
    </button>
  );
};

/* ─── Send-out dialog ───────────────────────────────────────────────── */

export const SendOutDialog = ({ rid, variant: initialVariant = "offer", prefillRecipients = null, onClose }) => {
  const [variant, setVariant] = useState(initialVariant);
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [adhocEmail, setAdhocEmail] = useState("");
  const create = useCreateSendOut();

  // Resolver runs once per rid via react-query — no setState-in-effect.
  // In Resend mode (prefillRecipients set), we SKIP the resolver entirely.
  const resolverQuery = useQuery({
    queryKey: ["send-out-resolver", rid],
    queryFn: () => axios.post(`${API}/customer-routing/resolve`, {
      logistics_request_id: rid, trace: true,
    }).then((r) => r.data),
    enabled: !!rid && !prefillRecipients,
    retry: false,
  });
  const resolverState = {
    loading: resolverQuery.isLoading,
    data: resolverQuery.data,
    err: resolverQuery.error?.response?.data?.detail?.message || resolverQuery.error?.message || null,
  };

  // Derive recipients lazily from resolver data the first time it arrives.
  // After the user touches the list, `recipientsTouched` becomes true and
  // we stop deriving — purely controlled state from then on.
  const [recipientsTouched, setRecipientsTouched] = useState(false);
  const [recipientsEdit, setRecipientsEdit] = useState([]);
  const derivedRecipients = useMemo(() => {
    // Resend mode wins: use the snapshotted recipients verbatim, skip
    // the resolver entirely.
    if (prefillRecipients && prefillRecipients.length > 0) return prefillRecipients;
    if (!resolverState.data) return [];
    const initial = [];
    if (resolverState.data.primary) initial.push({
      contact_id: resolverState.data.primary.id,
      email: resolverState.data.primary.email,
      name: resolverState.data.primary.name,
      kind: "to",
    });
    for (const c of resolverState.data.cc || []) initial.push({
      contact_id: c.id, email: c.email, name: c.name, kind: "cc",
    });
    return initial;
  }, [resolverState.data, prefillRecipients]);
  const recipients = recipientsTouched ? recipientsEdit : derivedRecipients;
  const setRecipients = (next) => {
    setRecipientsEdit(typeof next === "function" ? next(recipients) : next);
    setRecipientsTouched(true);
  };
  const masterId = resolverState.data?.primary?.customer_master_id
    || resolverState.data?.cc?.[0]?.customer_master_id
    || null;
  const masterDetailQuery = useQuery({
    queryKey: ["send-out-master-detail", masterId],
    queryFn: () => axios.get(`${API}/customer-masters/${masterId}`).then((r) => r.data),
    enabled: !!masterId,
    retry: false,
  });
  const masterContacts = (masterDetailQuery.data?.contacts || [])
    .filter((c) => c.is_active !== false);

  const addContact = (c, kind) => {
    if (recipients.some((r) => r.contact_id === c.id)) {
      toast.info(`${c.name} already added`);
      return;
    }
    setRecipients([...recipients, { contact_id: c.id, email: c.email, name: c.name, kind }]);
  };
  const addAdhoc = () => {
    if (!adhocEmail || !/.+@.+\..+/.test(adhocEmail)) {
      toast.error("Enter a valid email");
      return;
    }
    setRecipients([...recipients, { email: adhocEmail, kind: "cc" }]);
    setAdhocEmail("");
  };
  const removeAt = (idx) => setRecipients(recipients.filter((_, i) => i !== idx));
  const toggleKind = (idx) => setRecipients(recipients.map((r, i) =>
    i === idx ? { ...r, kind: r.kind === "to" ? "cc" : "to" } : r,
  ));

  const submit = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    try {
      await create.mutateAsync({
        logistics_request_id: rid,
        variant, channel,
        subject: subject || null,
        notes: notes || null,
        recipients,
        matched_rule_id: resolverState.data?.matched_rule_id || null,
        matched_rule_name: resolverState.data?.matched_rule_name || null,
        no_master: !!resolverState.data?.no_master,
      });
      toast.success("Send-out recorded (Brevo MOCKED)");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    }
  };

  const unaddedContacts = useMemo(
    () => masterContacts.filter((c) => !recipients.some((r) => r.contact_id === c.id)),
    [masterContacts, recipients],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="send-out-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-md w-full max-w-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        style={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between">
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 18 }}>
            <Send size={16} className="inline mr-1" /> Send PDF
            <span className="ml-2" style={mono(10, "#aa6a00", 700, "0.6px")}>MOCKED — Brevo</span>
          </h2>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>

        {resolverState.loading && (
          <div style={mono(11, "#6b7388")}>
            <Loader2 size={12} className="animate-spin inline mr-1" /> Resolving recipients…
          </div>
        )}
        {resolverState.err && (
          <div className="rounded px-3 py-2 inline-flex items-center gap-2"
               style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#b3261e", ...mono(11), width: "100%" }}>
            <AlertCircle size={13} /> {resolverState.err}
          </div>
        )}

        {/* Resolver verdict banner */}
        {prefillRecipients ? (
          <div
            className="rounded p-2"
            style={{ background: "rgba(10,135,84,0.10)", border: "1px solid rgba(10,135,84,0.40)", color: "#0a8754", ...mono(11) }}
            data-testid="send-out-resend-banner"
          >
            <Send size={12} className="inline mr-1" />
            <strong>Resend mode</strong> — recipients reused from previous send-out (resolver skipped).
          </div>
        ) : resolverState.data && (
          <div
            className="rounded p-2"
            style={{
              background: resolverState.data.matched_rule_id ? "rgba(78,208,132,0.10)" : "rgba(243,177,74,0.10)",
              border: resolverState.data.matched_rule_id ? "1px solid rgba(78,208,132,0.40)" : "1px solid rgba(243,177,74,0.40)",
              color: resolverState.data.matched_rule_id ? "#1c6b3d" : "#aa6a00",
              ...mono(11),
            }}
            data-testid="send-out-resolver-banner"
          >
            {resolverState.data.matched_rule_id ? (
              <>
                <CheckCircle2 size={12} className="inline mr-1" />
                Rule <strong>{resolverState.data.matched_rule_name}</strong> auto-suggested below — adjust freely.
              </>
            ) : resolverState.data.no_master ? (
              <>
                <AlertCircle size={12} className="inline mr-1" />
                No customer-master for this deal. Pick contacts manually.
              </>
            ) : (
              <>
                <FlaskConical size={12} className="inline mr-1" />
                No rule matched — pick contacts manually from the list below.
              </>
            )}
          </div>
        )}

        {/* Variant + channel */}
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Variant</div>
            <select value={variant} onChange={(e) => setVariant(e.target.value)} style={inputStyle} data-testid="send-out-variant">
              <option value="offer">Offer</option>
              <option value="billing">Billing</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label className="block col-span-2">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Channel</div>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} style={inputStyle} data-testid="send-out-channel">
              <option value="email">Email (MOCKED)</option>
              <option value="manual">Manual (just log)</option>
              <option value="other">Other</option>
            </select>
          </label>
        </div>

        {/* Recipients editor */}
        <div>
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Recipients</div>
          {recipients.length === 0 && (
            <div className="rounded p-2 text-center" style={{ background: "rgba(0,0,0,0.03)", border: "1px dashed rgba(0,0,0,0.15)", ...mono(11, "#6b7388") }} data-testid="send-out-no-recipients">
              No recipients yet. Add one below.
            </div>
          )}
          <ul className="space-y-1">
            {recipients.map((r, i) => (
              <li key={i} className="flex items-center gap-2 rounded p-2"
                  style={{ background: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.08)" }}
                  data-testid={`send-out-recipient-${i}`}>
                <button onClick={() => toggleKind(i)} type="button"
                        style={{ ...mono(9, r.kind === "to" ? "#7e3aa8" : "#6b7388", 700, "0.6px"), textTransform: "uppercase", background: r.kind === "to" ? "rgba(126,58,168,0.10)" : "rgba(0,0,0,0.05)", border: r.kind === "to" ? "1px solid rgba(126,58,168,0.30)" : "1px solid rgba(0,0,0,0.10)", padding: "3px 8px", borderRadius: 4, cursor: "pointer" }}
                        title="Toggle TO/CC">
                  {r.kind}
                </button>
                <span style={mono(11)}><Mail size={11} className="inline mr-1" />{r.email}</span>
                {r.name && <span style={mono(10, "#6b7388")}>({r.name})</span>}
                <button onClick={() => removeAt(i)} type="button" style={{ marginLeft: "auto", background: "transparent", border: "none", color: "#b3261e", cursor: "pointer" }} data-testid={`send-out-remove-${i}`}><Trash2 size={12} /></button>
              </li>
            ))}
          </ul>
          {unaddedContacts.length > 0 && (
            <div className="mt-2">
              <div style={mono(9, "#6b7388", 700, "1px")}>OTHER CONTACTS</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {unaddedContacts.map((c) => (
                  <button key={c.id} type="button" onClick={() => addContact(c, "cc")}
                          style={{ ...mono(10, "#1769aa"), background: "rgba(23,105,170,0.08)", border: "1px solid rgba(23,105,170,0.25)", padding: "3px 8px", borderRadius: 4, cursor: "pointer" }}
                          data-testid={`send-out-add-contact-${c.id}`}>
                    + {c.name} <span style={mono(9, "#6b7388")}>({c.role})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <input value={adhocEmail} onChange={(e) => setAdhocEmail(e.target.value)} placeholder="ad-hoc@example.com" type="email" style={inputStyle} data-testid="send-out-adhoc-email" />
            <button type="button" onClick={addAdhoc} style={{ ...mono(10, "#7e3aa8", 700, "0.8px"), textTransform: "uppercase", background: "rgba(126,58,168,0.10)", border: "1px solid rgba(126,58,168,0.30)", padding: "6px 12px", borderRadius: 5, cursor: "pointer", whiteSpace: "nowrap" }} data-testid="send-out-adhoc-add"><Plus size={11} className="inline mr-1" /> Add</button>
          </div>
        </div>

        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Subject</div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`${variant.charAt(0).toUpperCase()}${variant.slice(1)} — ${rid}`} style={inputStyle} data-testid="send-out-subject" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Notes (mocked email body preview)</div>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={inputStyle} data-testid="send-out-notes" />
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} style={{ ...mono(11, "#6b7388", 700, "0.6px"), textTransform: "uppercase", background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "8px 14px", borderRadius: 5, cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={submit} disabled={create.isPending || recipients.length === 0}
                  style={{ ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#0a8754", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 5, cursor: "pointer", opacity: (create.isPending || recipients.length === 0) ? 0.6 : 1 }}
                  data-testid="send-out-submit">
            {create.isPending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <Send size={12} className="inline mr-1" />}
            Send (mock)
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Audit trail panel ─────────────────────────────────────────────── */

export const SendOutsPanel = ({ rid }) => {
  const { data, isLoading } = useSendOuts(rid);
  const [resendState, setResendState] = useState(null); // {variant, recipients} | null
  const items = data?.items || [];
  return (
    <section
      className="rounded-md p-4 mt-4"
      style={{ background: "var(--bg-surface, #fff)", border: "1px solid rgba(10,135,84,0.30)" }}
      data-testid="send-outs-panel"
    >
      <header className="flex items-center justify-between mb-3">
        <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15 }}>
          <History size={14} className="inline mr-1" color="#0a8754" />
          Send-out trail ({items.length})
          <span className="ml-2" style={mono(9, "#aa6a00", 700, "0.6px")}>MOCKED · audit only</span>
        </h2>
      </header>
      {isLoading && <div style={mono(11, "#6b7388")}>Loading…</div>}
      {!isLoading && items.length === 0 && (
        <div className="text-center py-3" style={mono(11, "#6b7388")} data-testid="send-outs-empty">
          No send-outs yet.
        </div>
      )}
      {items.map((s) => (
        <div key={s.id} className="rounded p-3 mb-2"
             style={{ background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.08)" }}
             data-testid={`send-out-row-${s.id}`}>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <strong style={mono(12)}>{s.variant.toUpperCase()}</strong>
            <span style={{ ...mono(10, "#0a8754", 700, "0.6px"), textTransform: "uppercase" }}>{s.channel}</span>
            <span style={mono(10, "#6b7388")}><Clock size={10} className="inline mr-0.5" /> {(s.sent_at || "").slice(0, 16).replace("T", " ")}</span>
            <span style={mono(10, "#6b7388")}>by {s.sent_by_name}</span>
            {s.mocked && <span style={{ ...mono(9, "#aa6a00", 700, "0.6px"), textTransform: "uppercase" }}>· mocked</span>}
            {/* Phase F.4 — Resend affordance: re-opens the dialog
                pre-filled with the same variant + recipients. Common
                "they didn't see it" workflow. */}
            <button
              type="button"
              onClick={() => setResendState({
                variant: s.variant,
                recipients: (s.recipients || []).map((r) => ({
                  contact_id: r.contact_id || null,
                  email: r.email,
                  name: r.name,
                  kind: r.kind || "to",
                })),
              })}
              className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded"
              style={{ background: "rgba(10,135,84,0.10)", border: "1px solid rgba(10,135,84,0.30)", color: "#0a8754", ...mono(9, "#0a8754", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
              data-testid={`send-out-resend-${s.id}`}
              title="Resend with the same recipients"
            >
              <Send size={9} /> Resend
            </button>
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {(s.recipients || []).map((r, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
                    style={{ background: r.kind === "to" ? "rgba(126,58,168,0.10)" : "rgba(0,0,0,0.05)", border: "1px solid rgba(0,0,0,0.10)", ...mono(10) }}>
                <span style={{ ...mono(9, r.kind === "to" ? "#7e3aa8" : "#6b7388", 700, "0.6px"), textTransform: "uppercase" }}>{r.kind}</span>
                {r.name || r.email}
                {r.name && <span style={mono(9, "#6b7388")}>· {r.email}</span>}
              </span>
            ))}
          </div>
          {s.resolver_meta?.matched_rule_name && (
            <div className="mt-1" style={mono(10, "#6b7388")}>
              <ArrowRight size={10} className="inline mr-0.5" />
              Rule: {s.resolver_meta.matched_rule_name}
            </div>
          )}
        </div>
      ))}
      {resendState && (
        <SendOutDialog
          rid={rid}
          variant={resendState.variant}
          prefillRecipients={resendState.recipients}
          onClose={() => setResendState(null)}
        />
      )}
    </section>
  );
};
